import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UserGroupIcon, 
  UserIcon,
  CogIcon,
  XMarkIcon 
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

function PanelSelectionModal({ isOpen, onClose, user, showSuperAdmin = false }) {
  const navigate = useNavigate();
  const { login } = useAuth();

  const handlePanelSelection = async (panelType) => {
    // Super admin should never access user dashboard
    if (user?.isSuperAdmin && user?.email === 'admin@mastersunion.org' && panelType === 'employee') {
      window.location.href = '/';
      return;
    }
    
    if (panelType === 'manager') {
      onClose();
      navigate('/manager-dashboard', { replace: true });
    } else if (panelType === 'employee') {
      // Navigate to user dashboard - managers with role 'user' should be able to access
      // Use window.location.href for reliable navigation
      onClose();
      window.location.href = '/user-dashboard';
    } else if (panelType === 'admin') {
      // Generate a new token with manager-admin role
      try {
        const response = await axios.post('/api/auth/switch-to-admin', {
          email: user.email
        });
        
        // Update the auth context with new token and user data
        login(response.data.token, response.data.user);
        
        // Navigate to admin dashboard
        navigate('/', { replace: true });
        onClose();
        return;
      } catch (error) {
        console.error('Failed to switch to admin panel:', error);
        // Fallback: navigate anyway
        navigate('/', { replace: true });
        onClose();
      }
    }
  };

  if (!isOpen) return null;
  
  // Don't show if user is null (e.g., after logout)
  if (!user) return null;
  
  // Never show panel selection for super admin
  if (user?.isSuperAdmin && user?.email === 'admin@mastersunion.org') {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Choose Your Panel
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          <p className="text-sm text-gray-600 mb-6">
            Welcome back, {user?.firstName} {user?.lastName}! 
            Choose how you'd like to access the system:
          </p>

          <div className="space-y-4">
            {/* Manager Dashboard Option */}
            <button
              onClick={() => handlePanelSelection('manager')}
              className="w-full p-4 border-2 border-blue-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <UserGroupIcon className="h-8 w-8 text-blue-600" />
                </div>
                <div className="text-left">
                  <h4 className="text-lg font-medium text-gray-900">
                    Manager Dashboard
                  </h4>
                  <p className="text-sm text-gray-600">
                    View team attendance, approve requests, and manage your direct reports
                  </p>
                </div>
              </div>
            </button>

            {/* Employee Account Option */}
            <button
              onClick={() => handlePanelSelection('employee')}
              className="w-full p-4 border-2 border-green-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <UserIcon className="h-8 w-8 text-green-600" />
                </div>
                <div className="text-left">
                  <h4 className="text-lg font-medium text-gray-900">
                    Employee Account
                  </h4>
                  <p className="text-sm text-gray-600">
                    Access your personal dashboard - view your attendance, requests, and profile
                  </p>
                </div>
              </div>
            </button>

            {/* Super Admin Option - Only show for users with isManager=true */}
            {showSuperAdmin && (
              <button
                onClick={() => handlePanelSelection('admin')}
                className="w-full p-4 border-2 border-purple-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <CogIcon className="h-8 w-8 text-purple-600" />
                  </div>
                  <div className="text-left">
                    <h4 className="text-lg font-medium text-gray-900">
                      Super Admin
                    </h4>
                    <p className="text-sm text-gray-600">
                      Full admin access to your department(s) - manage users, attendance, and reports
                    </p>
                  </div>
                </div>
              </button>
            )}
          </div>

          <div className="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-800">
              <strong>Note:</strong> Choose the dashboard that best fits your current needs. You can always access other views through the navigation menu.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PanelSelectionModal;
